import { useState, useEffect, FC } from 'react';
import axios, { AxiosError } from 'axios';
import { DialogForError, DialogForHumanReadableError } from '../components';
import { toCamelCaseFormat, toSnakeCaseFormat } from '../utils/formatters';

type Props = {
  unauthorizedRedirectUrl?: string;
};

interface ServerError {
  description?: string;
  fail_description?: string;
  human?: boolean;
  detail?: string | { msg: string }[];
}

/**
 * @summary Перехватчик запросов axios-а.
 *
 * @param unauthorizedRedirectUrl - Url, по которому axios будет
 * редиректить в случае ошибки 401 Unauthorized. По умолчанию равен
 * '/login'.
 *
 * @description С помощью этого компонента мы можем
 * обрабатывать запросы axios-а и внедрять промежуточную логику.
 * К примеру, в этом компоненте перехватываются все неудачные запросы
 * к бэкэнду, и после получения ошибки на экране пользователя появляется
 * красивое окошко с описанием ошибки.
 */
export const AxiosInterceptor: FC<Props> = ({ unauthorizedRedirectUrl = '/login' }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [failedUrl, setFailedUrl] = useState('');
  const [description, setDescription] = useState('');
  const [failDescription, setFailDescription] = useState('');
  const [isHumanReadableError, setIsHumanReadableError] = useState(false);
  const utf8Decoder = new TextDecoder();

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const data = config.data;

      //  В Ajax могут приходить multipart/form-data, файлы и прочая ересь.
      // Нужно делать преобразования только для массивов и обьектов.
      if (
        (typeof data === 'object' && Object.getPrototypeOf(data).constructor === Object) ||
        Array.isArray(data)
      ) {
        config.data = toSnakeCaseFormat(data);
      }

      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        if ((response.config as any).toCamelCaseFormat) {
          response.data = toCamelCaseFormat(response.data);
        }

        return response;
      },
      (error: AxiosError<ServerError>) => {
        const status = error.response?.status;

        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        let responseData: ServerError = {};

        if (error.response?.data instanceof ArrayBuffer) {
          const decoded = utf8Decoder.decode(new Uint8Array(error.response.data));
          responseData = JSON.parse(decoded);
        } else if (error.response?.data) {
          responseData = error.response.data;
        }

        const config = error.config as any;

        if (status === 542) {
          setFailedUrl(config?.url || '');
          setDescription(responseData.description || '');
          setFailDescription(responseData.fail_description || '');
          setIsHumanReadableError(responseData.human ?? false);
          setOpenDialog(true);
        } else if (config?.notErrorMessageBox) {
          /*
         1. Форма авторизации: Там нужен перехват 401, поэтому тут просто ничего не делаем.
         2. Prometheus: 401 может возникать. Но пусть тогда будет молчок вместо перекидона
            на страницу авторизации. Не страшно.
         */
        } else if (status === 401) {
          if (window.location.pathname !== unauthorizedRedirectUrl) {
            window.location.href = unauthorizedRedirectUrl;
          }
        } else {
          const textErrorDetail = Array.isArray(responseData.detail)
            ? responseData.detail[0]?.msg ?? ''
            : responseData.detail ?? '';

          setFailedUrl(config?.url || '');
          setDescription(`${status}: ${textErrorDetail}`);
          setFailDescription(responseData.fail_description || '');
          setIsHumanReadableError(responseData.human ?? false);
          setOpenDialog(true);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [unauthorizedRedirectUrl]);

  return (
    <>
      {isHumanReadableError ? (
        <DialogForHumanReadableError
          description={description}
          open={openDialog}
          onClose={() => setOpenDialog(false)}
        />
      ) : (
        <DialogForError
          url={failedUrl}
          description={description}
          failDescription={failDescription}
          open={openDialog}
          onClose={() => setOpenDialog(false)}
        />
      )}
    </>
  );
};
